// Defects inbox: every defect the caller can see, across all assets, with the
// discussion thread one click away. Complements the per-asset lists in Asset
// View; this is where chat activity gets noticed (the top-bar callout deep
// links here with ?defectId= to auto-open a thread).
//
// Data: GET defect/unread (server enforces scope: inspectors see all, others
// company + child companies; rows arrive unread-first then latest activity).
// Refreshes on mount, window focus, a slow interval, socket activity pings,
// and after any thread action.

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ResolveDefectDialog from '../../components/defects/ResolveDefectDialog';
import moment from 'moment';
import { baseUrl } from '../../shared';
import { StatusChip } from '../../components/ui';
import DefectChatModal from '../../components/defects/DefectChatModal';
import { getDefectSocket } from '../../components/defects/defectSocket';

const REFRESH_MS = 60000;

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    username: localStorage.getItem('username'),
    bToken: localStorage.getItem('bToken'),
  };
}

export default function Defects() {
  const location = useLocation();
  const navigate = useNavigate();
  const [defects, setDefects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState('open');
  const [filter, setFilter] = useState('');
  const [chatDefectId, setChatDefectId] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null);

  const load = useCallback(() => {
    fetch(baseUrl + 'defect/unread', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((json) => {
        setDefects(Array.isArray(json) ? json : []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    window.addEventListener('focus', load);
    const socket = getDefectSocket();
    if (socket) socket.on('defect:activity', load);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', load);
      if (socket) socket.off('defect:activity', load);
    };
  }, [load]);

  // Deep link from the top-bar callout: /defects?defectId=N opens that thread.
  useEffect(() => {
    const param = new URLSearchParams(location.search).get('defectId');
    if (param) setChatDefectId(Number.parseInt(param, 10) || null);
  }, [location.search]);

  const openAsset = (defect) => navigate('/assets?assetNo=' + encodeURIComponent(defect.assetId));

  // Asset filter: case-insensitive substring on assetId (primary identifier)
  // with catNo as the secondary match, per the shared identity rule.
  const query = filter.trim().toLowerCase();
  const rows = defects
    .filter((d) => (tab === 'open' ? !d.resolvedAt : !!d.resolvedAt))
    .filter((d) => !query
      || String(d.assetId || '').toLowerCase().includes(query)
      || String(d.catNo || '').toLowerCase().includes(query));

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Defects</Typography>
      <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', mb: 2 }}>
        Defect reports and their discussions across all assets you can see. Unread activity sorts to the top.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={tab}
          onChange={(e, v) => { if (v) setTab(v); }}
        >
          <ToggleButton value="open" sx={{ textTransform: 'none', px: 2 }}>Open</ToggleButton>
          <ToggleButton value="completed" sx={{ textTransform: 'none', px: 2 }}>Completed</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Filter by Asset ID"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'var(--muted)' }} />
              </InputAdornment>
            ),
            endAdornment: filter ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setFilter('')} aria-label="clear filter">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {!loaded ? (
        <Typography variant="body2" sx={{ color: 'var(--muted)' }}>Loading defects...</Typography>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <Typography variant="body2" sx={{ color: 'var(--muted)' }}>
            {query
              ? 'No defects match your filter.'
              : tab === 'open' ? 'No open defects.' : 'No completed defects.'}
          </Typography>
        </Paper>
      ) : (
        rows.map((d) => (
          <Paper
            key={d.id}
            onClick={() => setChatDefectId(d.id)}
            sx={{
              p: 2, mb: 1.5, cursor: 'pointer',
              background: 'var(--surface)', border: '1px solid var(--border)',
              '&:hover': { borderColor: 'var(--primary)' },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography variant="body2" className="aims-mono" sx={{ fontWeight: 700, color: 'var(--text)' }}>
                  {d.assetId}
                </Typography>
                {d.priority ? <StatusChip domain="defect" value={d.priority} /> : null}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'var(--muted)', flexShrink: 0 }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">{d.messageCount ?? 0}</Typography>
                {d.unreadCount > 0 && (
                  <Box component="span" sx={{ background: 'var(--primary)', color: '#fff', borderRadius: '10px', px: 0.75, py: 0.1, fontSize: 11, fontWeight: 700, lineHeight: 1.6 }}>
                    {d.unreadCount} new
                  </Box>
                )}
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'var(--text)', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {d.defectComment}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--muted)', display: 'block', mt: 0.5 }}>
              {d.customer}{d.siteLocation ? ' · ' + d.siteLocation : ''}
              {' · '}
              {d.lastMessageAt
                ? 'Last activity ' + moment(d.lastMessageAt).format('DD-MM-YYYY hh:mm A')
                : 'Reported ' + moment(d.createdAt).format('DD-MM-YYYY hh:mm A')}
            </Typography>
          </Paper>
        ))
      )}

      <DefectChatModal
        open={chatDefectId !== null}
        defectId={chatDefectId}
        onClose={() => setChatDefectId(null)}
        onMarkComplete={(id) => {
          const target = defects.find((d) => d.id === id);
          if (target) setResolveTarget(target);
        }}
        onOpenAsset={openAsset}
        onReportNew={() => {
          const target = defects.find((d) => d.id === chatDefectId);
          if (target) openAsset(target);
        }}
        onChanged={load}
      />

      <ResolveDefectDialog
        defect={resolveTarget}
        onClose={() => setResolveTarget(null)}
        onResolved={load}
      />
    </Box>
  );
}
